import { Image, Typography } from "antd";
import QuixLogo from "assets/Logo.png";
import classes from "./styles.module.css";
import { useEffect } from "react";
import { Link } from "react-router-dom";

function EndUserStatement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.container}>
      {/* Quix Image */}
      <Link to={"/register"} className={classes.logoContainer}>
        <Image src={QuixLogo} alt="Quix Logo" preview={false} />
      </Link>

      <Typography.Title level={1}>
        End-User Statement for Quantum Computing Cloud Services
      </Typography.Title>

      <Typography.Paragraph>
        This End-User Statement (EUS) is provided in support of the export control compliance
        requirements as per the laws and regulations governing the export of Quantum Computing (QC)
        cloud services. The undersigned, as the authorized representative of the end-user (referred
        to hereinafter as the &quot;End- User&quot;), hereby certifies and agrees to the following
        terms and conditions regarding the use of high-performance computing cloud services provided
        by QuiX Quantum BV (referred to hereinafter as the "Service Provider")
      </Typography.Paragraph>

      <Typography.Title level={3}>1. Scope of Use</Typography.Title>
      <Typography.Paragraph>
        We encourage all users to be mindful of the following considerations when using our
        platform:
      </Typography.Paragraph>
      <Typography.Paragraph>
        <ul>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Legal and Ethical Use:
              </Typography.Title>
              Our cloud services are intended for lawful and ethical purposes only. Users are
              strictly prohibited from using the platform to develop, store, or facilitate
              activities related to weapons of mass destruction, illegal surveillance, hacking, or
              any other harmful activity that could endanger public safety or violate human rights.
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Monitoring and Compliance:
              </Typography.Title>
              We actively monitor usage to ensure compliance with international laws and regulations
              regarding dual-use technologies, such as export control regulations, sanctions, and
              other restrictions on the misuse of cloud services for harmful purposes.
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Security and Privacy:
              </Typography.Title>
              Our platform is designed to protect user data and privacy. However, users are
              responsible for ensuring that their use of the service does not result in the
              unauthorized dissemination or misuse of sensitive data, including data that could be
              repurposed for malicious ends.
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Prohibited Activities:
              </Typography.Title>
              Activities that could be considered dual-use and are strictly prohibited on our
              platform include, but are not limited to:
              <ul>
                <li>Development or distribution of harmful software or cyberweaponry.</li>
                <li>
                  Hosting or supporting services that promote terrorism, violence, or other illegal
                  actions.
                </li>
                <li>
                  Use of cloud services to violate export control or trade compliance laws,
                  especially in relation to sensitive technologies or intellectual property.
                </li>
              </ul>
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Paragraph>Reporting Misuse:</Typography.Paragraph>

            <Typography.Text>
              We take the misuse of our platform seriously. If you become aware of any dual-use
              activities or potential violations, please report them to our compliance team for
              immediate investigation.
            </Typography.Text>
          </li>
          <Typography.Text>
            By using our QC cloud service, you agree to abide by these dual-use policies and to help
            ensure that our platform is used only for positive, lawful, and beneficial purposes. We
            reserve the right to suspend or terminate accounts that violate these principles.
          </Typography.Text>
          <Typography.Text>
            We are dedicated to promoting the responsible use of technology to foster innovation
            while safeguarding against potential abuses. The End-User acknowledges and confirms that
            the QC cloud services will be used solely for the following lawful purposes:
          </Typography.Text>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Intended Use:
              </Typography.Title>
              scientific research, simulations and optimization problems, data analytics, quantum
              machine learning, for developments and experimentation in fields such as materials
              science, quantum chemistry, logistics, engineering, marketing, automotive and
              pharmaceutical.
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Paragraph>
              <Typography.Title level={5} underline>
                Nature of Use:
              </Typography.Title>
              The intended usage is exclusively for peaceful, non-military, and non-restricted
              purposes. The services will not be used for activities related to the development,
              production, or use of nuclear, chemical, biological weapons, weapons of mass
              destruction, or any other restricted technologies under national or international
              laws, including but not limited to military technology or conventional arms
              development. The End-User will ensure that the service will not be diverted or used by
              any entities or individuals on international sanctions lists, including those related
              to terrorism, arms proliferation, or embargoes.
            </Typography.Paragraph>
          </li>
        </ul>
      </Typography.Paragraph>

      <Typography.Title level={3}>2. Compliance with Export Control Regulations</Typography.Title>
      <Typography.Paragraph>
        The End-User agrees to comply with all applicable export control laws and regulations, and
        agrees not to export, re-export, transfer, or share any controlled data or technology to any
        individuals or entities prohibited by law, nor will it allow access to controlled data by
        nationals of sanctioned countries or entities on export control lists without appropriate
        licensing and approval from the relevant authorities.
      </Typography.Paragraph>

      <Typography.Title level={3}>3. Prohibited End-Uses and End-Users</Typography.Title>
      <Typography.Paragraph>
        The End-User certifies that the QCC cloud services will not be used for any prohibited
        end-uses, including but not limited to:
        <ul>
          <li>
            The design, development, production, or use of nuclear, chemical, or biological weapons
            or their delivery systems.
          </li>
          <li>
            The creation, dissemination, or support of cyber-attacks, hacking activities, or any
            form of digital espionage.
          </li>
          <li>
            Military or paramilitary applications, defense or intelligence-related activities,
            unless such use is explicitly permitted by applicable laws and authorized under an
            export license.
          </li>

          <Typography.Paragraph>
            Additionally, the End-User certifies that the QC services will not be made available to
            any:
          </Typography.Paragraph>

          <li>
            Persons, organizations, or entities listed on government sanctions lists (including but
            not limited to the European Union&#39;s Directorate-General for Financial Stability,
            Financial Services and Capital Markets Union (DG FISMA) and the European External Action
            Service (EEAS), through its Sanctions Policy Division, for designing and coordinating EU
            sanctions policy.{" "}
          </li>
          <li>
            Any end-user engaged in the proliferation of weapons of mass destruction or missile
            technology.
          </li>
        </ul>
      </Typography.Paragraph>

      <Typography.Title level={3}>4. User Access Control</Typography.Title>
      <Typography.Paragraph>
        The End-User agrees to implement and maintain strict access control mechanisms to ensure
        that only authorized individuals within the organization have access to the QC cloud
        services. The End-User shall take all necessary precautions to prevent unauthorized access,
        distribution, or use of controlled data or technologies hosted on or transmitted through the
        QC cloud service. <br /> 
        The End-User is responsible for ensuring that any sub-users or affiliates
        comply with this End-User Statement and the applicable export control laws.
      </Typography.Paragraph>

      <Typography.Title level={3}>5. Data Classification and Usage</Typography.Title>
      <Typography.Paragraph>
        The End-User acknowledges that any data hosted, processed, or transferred using the QC cloud
        services that is classified as &quot;export-controlled&quot; under applicable laws must be
        appropriately identified and managed to prevent unauthorized access or export. This
        includes:
        <ul>
          <li>Implementing encryption measures to protect sensitive data.</li>
          <li>Segregating controlled data from non-controlled data where necessary.</li>
          <li>
            Ensuring proper licensing or authorization is obtained for any export of controlled
            data.
          </li>
        </ul>
      </Typography.Paragraph>

      <Typography.Title level={3}>6. Reporting and Record-Keeping</Typography.Title>
      <Typography.Paragraph>
        The End-User agrees to maintain accurate records of the use of QC cloud services in
        compliance with applicable export control laws. These records will include:
        <ul>
          <li>Details of data processed, stored, or transferred using the QC cloud services.</li>
          <li>
            Names and contact information of individuals with access to export-controlled data.
          </li>
          <li>
            Documentation of any export licenses or authorizations obtained for controlled data.
          </li>
        </ul>
        The End-User agrees to provide these records upon request by the relevant export control
        authorities or the Service Provider as part of any compliance audit.
      </Typography.Paragraph>

      <Typography.Title level={3}>7. Licensing Requirements</Typography.Title>
      <Typography.Paragraph>
        If the End-User intends to use the QC cloud services for purposes that may require export
        licensing, the End-User agrees to obtain all necessary licenses and provide proof of such
        licenses to the Service Provider before using the services for any controlled activities.{" "}
        <br />
        The End-User understands that failure to obtain required licenses or to comply with
        licensing terms may result in the suspension or termination of access to the QC cloud
        services.
      </Typography.Paragraph>

      <Typography.Title level={3}>
        8. Acknowledgement of Export Control Obligations
      </Typography.Title>
      <Typography.Paragraph>
        The End-User acknowledges that export control compliance is a critical obligation and that
        violations of export control laws can result in severe legal penalties, including fines,
        imprisonment, and restrictions on future access to technologies. <br />
        The End-User agrees to take all necessary measures to ensure compliance with this End-User
        Statement and applicable export control laws.
      </Typography.Paragraph>

      <Typography.Title level={3}>9. Indemnification</Typography.Title>
      <Typography.Paragraph>
        The End-User agrees to indemnify and hold harmless the Service Provider from any claims,
        penalties, or liabilities arising from any breach of this End-User Statement or
        non-compliance with export control laws by the End-User or its affiliates.
      </Typography.Paragraph>

      <Typography.Title level={3}>10. Declaration</Typography.Title>
      <Typography.Paragraph>
      By accepting this end-user statement, I certify that the information provided in this End-User Statement is
        true, accurate, and complete. I understand the obligations imposed by export control laws
        and agree to comply with all applicable requirements. <br />
        This End-User Statement forms part of the legally binding agreement between the End-User and
        the Service Provider. Failure to comply with the terms outlined in this statement may result
        in termination of services. <br />
        This comprehensive statement ensures that end-users of quantum computing cloud services
        understand and commit to complying with international and national export control laws,
        providing the necessary guarantees and safeguards for providers of these services. <br />
        By signing, the End-User agrees to cooperate fully with any subsequent audits, inspections,
        or follow-up investigations required by government or regulatory authorities to ensure
        compliance with export control laws.
      </Typography.Paragraph>
    </div>
  );
}

export default EndUserStatement;
